import { inject, Injectable } from '@angular/core';
import { ActionSheetController } from '@ionic/angular/standalone';
import {
    Camera,
    CameraPluginPermissions,
    CameraResultType,
    CameraSource,
    GalleryImageOptions
} from '@capacitor/camera';
import { ImageOptions } from '@capacitor/camera/dist/esm/definitions';
import { AlertService } from './alert.service';
import { UserImageModel } from '@app/models';

@Injectable({
    providedIn: 'root',
})
export class TakePhotoService
{
    private imageQuality = 60;
    private actionSheetController = inject(ActionSheetController);
    private alertService = inject(AlertService);

    async webPathToFile(webPath: string, format: string): Promise<File>
    {
        const response = await fetch(webPath);
        const blob = await response.blob();

        return new File([blob], `${new Date().toISOString()}.${format}`, { type: blob.type, });
    }

    dataURLtoBlob(dataURI: string, fileName: string | null = null): File
    {
        const arr = dataURI.split(',');
        const mime = (arr[0].match(/:(.*?);/))![1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--)
        {
            u8arr[n] = bstr.charCodeAt(n);
        }

        if (fileName === null)
        {
            fileName = new Date().toISOString();
        }

        return new File([u8arr], fileName, { type: mime, });
    }

    blobToDataURL(blob: Blob): Promise<string>
    {
        return new Promise<string>((resolve, reject) =>
        {
            const reader = new FileReader();

            reader.onload = _e => resolve(reader.result as string);
            reader.onerror = _e => reject(reader.error);
            reader.onabort = _e => reject(new Error('Read aborted'));

            reader.readAsDataURL(blob);
        });
    }

    async showTakePhotoActions(imageQuality?: number): Promise<UserImageModel[] | null>
    {
        if (typeof (imageQuality) === 'undefined')
        {
            imageQuality = this.imageQuality;
        }

        const actionSheet = await this.actionSheetController.create({
            header: 'Select Image source',
            buttons: [
                {
                    text: 'Load from Gallery',
                    icon: 'images',
                    handler: async () =>
                    {
                        const images = await this.selectFromLibrary(imageQuality);
                        await actionSheet.dismiss(images);

                        return true;
                    },
                },
                {
                    text: 'Use Camera',
                    icon: 'camera',
                    handler: async () =>
                    {
                        const images = await this.takePhoto(imageQuality);
                        await actionSheet.dismiss(images);

                        return true;
                    },
                },
                {
                    text: 'Cancel',
                    role: 'cancel',
                },
            ],
        });
        await actionSheet.present();

        const { data } = await actionSheet.onDidDismiss();

        return data;
    }

    async selectFromLibrary(imageQuality?: number): Promise<UserImageModel[] | null>
    {
        const permissions: CameraPluginPermissions = {
            permissions: [
                'photos',
            ],
        };

        if (!await this.requestPermission(permissions))
        {
            return null;
        }

        if (typeof (imageQuality) === 'undefined')
        {
            imageQuality = this.imageQuality;
        }

        try
        {
            const options: GalleryImageOptions = {
                quality: imageQuality,
                width: 1000,
                height: 1000,
            };

            const { photos } = await Camera.pickImages(options);

            if (!photos)
            {
                await this.alertService.show('Some problem with the photo gallery, please try again.');

                return null;
            }

            const userImages: UserImageModel[] = [];

            for (const photo of photos)
            {
                if (!photo.webPath)
                {
                    await this.alertService.show('The photo format is not supported. Please try changing the camera settings.');

                    return null;
                }

                userImages.push({
                    format: photo.format,
                    webPath: photo.webPath,
                    file: await this.webPathToFile(photo.webPath, photo.format),
                });
            }

            return userImages;
        }
        catch (error: any)
        {
            console.error('selectFromLibrary', error);

            if (error && 'message' in error)
            {
                if (!error.message.toLowerCase().includes('user cancelled'))
                {
                    await this.alertService.show(error.message);
                }
            }
            else
            {
                await this.alertService.show('The app was not able to open photos gallery.');
            }

            return null;
        }
    }

    async takePhoto(imageQuality?: number): Promise<UserImageModel[] | null>
    {
        const permissions: CameraPluginPermissions = {
            permissions: [
                'camera',
            ],
        };

        if (!await this.requestPermission(permissions))
        {
            return null;
        }

        if (typeof (imageQuality) === 'undefined')
        {
            imageQuality = this.imageQuality;
        }

        try
        {
            const options: ImageOptions = {
                quality: imageQuality,
                resultType: CameraResultType.Uri,
                allowEditing: false,
                saveToGallery: false,
                source: CameraSource.Camera,
                width: 1000,
                height: 1000,
            };

            const image = await Camera.getPhoto(options);

            if (!image)
            {
                await this.alertService.show('Some problem with the Camera, please try again.');

                return null;
            }

            if (!image.webPath)
            {
                await this.alertService.show('The photo format is not supported. Please try changing the camera settings.');

                return null;
            }

            return [
                {
                    format: image.format,
                    webPath: image.webPath,
                    file: await this.webPathToFile(image.webPath, image.format),
                },
            ];
        }
        catch (error: any)
        {
            console.error('takePhoto', error);

            if (error && 'message' in error)
            {
                if (!error.message.toLowerCase().includes('user cancelled'))
                {
                    await this.alertService.show(error.message);
                }
            }
            else
            {
                await this.alertService.show('The app was not able to take a photo, please try again.');
            }

            return null;
        }
    }

    async getCameraPermissionsMessage(): Promise<string>
    {
        try
        {
            const cameraPermissions = await Camera.checkPermissions();
            console.info('await Camera.checkPermissions()', cameraPermissions);

            let message = `<div>Photo Permissions:</div><ul class="m-0">`;
            message += `<li>camera: <b>${cameraPermissions.camera}</b></li>`;
            message += `<li>gallery: <b>${cameraPermissions.photos}</b></li>`;
            message += `</ul>`;

            return message;
        }
        catch (error: any)
        {
            console.error('getCameraPermissionsMessage()', error);

            let message = `<div>Photo Permissions:</div><ul class="m-0">`;
            message += `<li>camera: <b>not able to check permissions</b></li>`;
            message += `<li>gallery: <b>not able to check permissions</b></li>`;
            message += `</ul>`;

            return message;
        }
    }

    private async requestPermission(permissions: CameraPluginPermissions): Promise<boolean>
    {
        try
        {
            const permissionStatus = await Camera.requestPermissions();
            console.info('await Camera.requestPermissions()', permissionStatus);

            for (const permission of permissions.permissions)
            {
                if (permissionStatus[permission] !== 'granted')
                {
                    await this.alertService.show('To take photos or select images from the gallery, please grant Camera and Gallery permissions in your device settings.');

                    return false;
                }
            }

            return true;
        }
        catch (error: any)
        {
            console.error('requestPermission', error);

            if (error && 'message' in error)
            {
                if (error.message.toLowerCase().includes('not implemented on web'))
                {
                    return true;
                }
            }

            await this.alertService.show('The app was not able to request the permissions, please grant Camera and Gallery permissions in your device settings.');

            return false;
        }
    }
}
